/**
 * Default Layout - Initial panel arrangement for spatial canvas
 * Provides default positioning and cascade logic for new panels
 */

import type { PanelState, PanelType } from './panel-layout-store';

/**
 * Get the default layout for new users
 * Arranges all core panels in a responsive grid based on viewport size
 */
export function getDefaultLayout(): Map<string, PanelState> {
  const panels: PanelState[] = [
    {
      id: 'avid-channels-default',
      type: 'avid-channels' as PanelType,
      position: { x: 20, y: 80 },
      size: { width: 340, height: 520 },
      zIndex: 1000,
      minimized: false,
    },
    {
      id: 'topics-default',
      type: 'topics' as PanelType,
      position: { x: 380, y: 80 },
      size: { width: 420, height: 520 },
      zIndex: 1001,
      minimized: false,
    },
    {
      id: 'notes-default',
      type: 'notes' as PanelType,
      position: { x: 820, y: 80 },
      size: { width: 320, height: 520 },
      zIndex: 1002,
      minimized: false,
    },
  ];

  return new Map(panels.map((p) => [p.id, p]));
}

/**
 * Calculate cascade position for a new panel
 * Ensures new panels stay within viewport bounds
 */
export function getCascadePosition(
  existingPanels: Map<string, PanelState>,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight
): { x: number; y: number } {
  const offset = 30;
  const count = existingPanels.size;
  const panelWidth = 350;
  const panelHeight = 350;

  const baseX = 20;
  const baseY = 90;

  let x = baseX + count * offset;
  let y = baseY + count * offset;

  // Ensure position + panel size doesn't exceed viewport
  const maxX = Math.max(baseX, viewportWidth - panelWidth - 20);
  const maxY = Math.max(baseY, viewportHeight - panelHeight - 20);

  // Constrain within bounds
  x = Math.min(x, maxX);
  y = Math.min(y, maxY);

  // Cascade wrapping: if too far down, wrap to next column
  if (y > viewportHeight - panelHeight - 100) {
    const col = Math.floor(count / 5);
    x = baseX + col * (panelWidth + offset);
    y = baseY;
  }

  return { x, y };
}

/**
 * Auto-layout panels into a tiled grid
 * Arranges all open panels left-to-right, top-to-bottom within the viewport.
 * Preserves zIndex and minimized state.
 */
export function autoLayoutPanels(panels: Map<string, PanelState>): Map<string, PanelState> {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const TOOLBAR_HEIGHT = 60;
  const MARGIN = 20;
  const GAP = 10;

  const availableWidth = viewportWidth - MARGIN * 2;
  const availableHeight = viewportHeight - TOOLBAR_HEIGHT - MARGIN;

  const count = panels.size;

  // Determine column count based on number of panels
  let cols: number;
  if (count <= 1) {
    cols = 1;
  } else if (count <= 2) {
    cols = 2;
  } else if (count <= 4) {
    cols = 2;
  } else {
    cols = 3;
  }

  const rows = Math.ceil(count / cols);

  const panelWidth = Math.floor((availableWidth - GAP * (cols - 1)) / cols);
  const panelHeight = Math.floor((availableHeight - GAP * (rows - 1)) / rows);

  const result = new Map<string, PanelState>();
  let index = 0;

  for (const [id, panel] of panels) {
    const col = index % cols;
    const row = Math.floor(index / cols);

    const x = MARGIN + col * (panelWidth + GAP);
    const y = TOOLBAR_HEIGHT + row * (panelHeight + GAP);

    result.set(id, {
      ...panel,
      position: { x, y },
      size: { width: panelWidth, height: panelHeight },
    });

    index++;
  }

  return result;
}

/**
 * Get a panel entry template by type for quick creation
 */
export const panelTemplates: Record<
  PanelType,
  {
    defaultSize: { width: number; height: number };
    minSize: { width: number; height: number };
  }
> = {
  discord: {
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
  },
  x: {
    defaultSize: { width: 550, height: 700 },
    minSize: { width: 350, height: 400 },
  },
  '3d': {
    defaultSize: { width: 800, height: 700 },
    minSize: { width: 400, height: 300 },
  },
  'spatial-grid': {
    defaultSize: { width: 800, height: 600 },
    minSize: { width: 400, height: 300 },
  },
  settings: {
    defaultSize: { width: 450, height: 600 },
    minSize: { width: 300, height: 400 },
  },
  identity: {
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
  },
  'linked-identities': {
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
  },
  business: {
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
  },
  receipts: {
    defaultSize: { width: 650, height: 700 },
    minSize: { width: 450, height: 400 },
  },
  metrics: {
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 400 },
  },
  'private-chat': {
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
  },
  pieces: {
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
  },
  troves: {
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
  },
  canvas: {
    defaultSize: { width: 550, height: 680 },
    minSize: { width: 400, height: 500 },
  },
  objects: {
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 400 },
  },
  'world-map': {
    defaultSize: { width: 900, height: 700 },
    minSize: { width: 600, height: 500 },
  },
  voice: {
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 500 },
  },
  feed: {
    defaultSize: { width: 550, height: 750 },
    minSize: { width: 350, height: 500 },
  },
  topics: {
    defaultSize: { width: 550, height: 750 },
    minSize: { width: 350, height: 500 },
  },
  notes: {
    defaultSize: { width: 480, height: 680 },
    minSize: { width: 320, height: 400 },
  },
  'meeting-notes': {
    defaultSize: { width: 520, height: 720 },
    minSize: { width: 380, height: 500 },
  },
  'avid-player': {
    defaultSize: { width: 520, height: 680 },
    minSize: { width: 380, height: 480 },
  },
  'avid-channels': {
    defaultSize: { width: 480, height: 700 },
    minSize: { width: 340, height: 480 },
  },
  'my-accords': {
    defaultSize: { width: 700, height: 800 },
    minSize: { width: 500, height: 600 },
  },
  'public-profile': {
    defaultSize: { width: 600, height: 750 },
    minSize: { width: 400, height: 500 },
  },
  'accord-detail': {
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 500 },
  },
  preferences: {
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 500 },
  },
  groups: {
    defaultSize: { width: 600, height: 750 },
    minSize: { width: 450, height: 600 },
  },
};
